/** @format */
import { omit } from 'lodash';

import { productConditionStringToEnum } from '@toucan/client/global/product';
import {
    Address,
    CustomerDetails,
    DeliverySummary,
    DeliveryType,
    DisputeItem,
    LineItemState,
    ProductItemSummary,
    PublicTransactionStage,
    TransactionKind,
    TransactionState,
    TransactionSummary,
    TransactionSummaryAddress,
} from '@toucan/client/global/transaction';

type TransformTransactionSummaryOptions = {
    itemsToSellStatesToExclude?: LineItemState[];
    allowUnknownCondition?: boolean;
};

type ItemSellingTransform = {
    itemsToSell: ProductItemSummary[];
    itemsToReturn: ProductItemSummary[];
};

export const transformCustomer = (
    customer: any
): { customerDetails: CustomerDetails } => {
    if (!customer) {
        // This is to replicate the pre-existing behaviour
        // without getting upset about types
        return { customerDetails: customer };
    }

    return {
        customerDetails: {
            ...customer,
            // This probably ought to throw if the api data is malformed.
            // Optional chain to preserve legacy behaviour until we have more
            // robust fixtures/testing in place for apis.
            emailAddress: customer?.emailAddress?.trim(),
        },
    };
};
export const transactionTemplate: TransactionSummary = {
    kind: TransactionKind.TRANSACTION_SUMMARY,
    id: '',
    market: '',
    reference: '',
    customerDetails: {
        firstName: '',
        lastName: '',
        emailAddress: '',
        phoneNumber: '',
        receiveOffersViaEmail: false,
        receiveOffersViaText: false,
    },
    customerId: -1,
    billingAddress: {
        id: 0,
        addressee: '',
        line1: '',
        line2: '',
        town: '',
        county: '',
        state: '',
        postcode: '',
        zipcode: '',
        country: '',
        deliveryDefault: false,
        billingDefault: false,
        nickname: '',
    },
    state: TransactionState.UNDEFINED,
    stage: PublicTransactionStage.UNDEFINED_STAGE,
    disputes: [],
    itemsToSell: [],
    itemsToBuy: [],
    itemsToReturn: [],
    collections: [],
    shipments: [],
    deliveries: [],
    totals: {
        totalIncTax: 0,
        totalExTax: 0,
        totalTax: 0,
        totalPayments: 0,
        balance: 0,
    },
    promotions: [],
    createdDate: '',
    modifiedDate: '',
    tax: [],
    isWithinRma: false,
};

/**
 * Sort the deliveries returned from the transaction API into
 * two buckets; collections and shipments.
 *
 * @param transaction
 */
function transformDeliveries(
    transaction: TransactionSummary
): TransactionSummary {
    const { deliveries, ...newTransaction } = transaction;

    if (!deliveries) {
        return {
            ...transaction,
            collections: [],
            shipments: [],
        };
    }

    // Match delivery type
    function deliveryTypeEquals(deliveryType: DeliveryType) {
        return function (delivery: any) {
            return delivery.deliveryType === deliveryType;
        };
    }

    // Ensure that a delivery option is in the correct shape
    function transformDeliveryData(delivery: any): DeliverySummary {
        const transformedData = {
            ...delivery,
            deliveryOption: parseInt(delivery.deliveryOption),
        };

        if (delivery.address) {
            transformedData.address = transformAddress(
                delivery.address as TransactionSummaryAddress
            );
        }

        return transformedData;
    }

    const collections = deliveries
        .filter(deliveryTypeEquals(DeliveryType.COLLECTION))
        .map(transformDeliveryData);

    const shipments = deliveries
        .filter(deliveryTypeEquals(DeliveryType.SHIPMENT))
        .map(transformDeliveryData);

    return {
        ...newTransaction,
        deliveries,
        collections,
        shipments,
    };
}

/**
 * Transforms a transaction summary address into a regular Address
 * by copying over the customerAddressId in place of the ID stored on the
 * transaction and ensuring that zipcode/state are used for US addresses.
 *
 * @param address
 * @return Address
 */
function transformAddress(address: TransactionSummaryAddress): Address {
    const updatedAddress = {
        ...omit(address, 'customerAddressId'),
        id: address.customerAddressId,
    };

    if (address.country === 'US') {
        updatedAddress.state = address.county;
        updatedAddress.zipcode = address.postcode;
        delete updatedAddress.county;
        delete updatedAddress.postcode;
    }

    return updatedAddress;
}

/**
 * Partition items to sell and items to return based on the item state
 * @param items
 */
function partitionItemsSelling(
    items: ProductItemSummary[]
): ItemSellingTransform {
    return items.reduce(
        (accumulator: ItemSellingTransform, item: ProductItemSummary) => {
            const key =
                item.state === LineItemState.RETURNING
                    ? 'itemsToReturn'
                    : 'itemsToSell';
            accumulator[key].push(item);
            return accumulator;
        },
        { itemsToSell: [], itemsToReturn: [] }
    );
}

export function transformTransactionSummary(
    data: any,
    {
        itemsToSellStatesToExclude = [
            LineItemState.CANCELLED,
            LineItemState.DECLINED,
        ],
        allowUnknownCondition = false,
    }: TransformTransactionSummaryOptions = {}
): TransactionSummary {
    const transactionSummary: TransactionSummary = {
        ...transactionTemplate,
        ...transformDeliveries(data),
        ...partitionItemsSelling(data.itemsToSell),
        ...transformCustomer(data.customer),
        customerId: data.customer?.customerId ?? transactionTemplate.customerId,
        isWithinRma: data.isWithinRma ?? transactionTemplate.isWithinRma,
    };

    if (data.billingAddress) {
        transactionSummary.billingAddress = transformAddress(
            data.billingAddress as TransactionSummaryAddress
        );
    }

    // Updates the ProductItemSummary response from the api to use an enum for the condition
    const updateItemConditionsToEnum = (item: any) => {
        return {
            ...item,
            condition: productConditionStringToEnum(
                item.condition,
                allowUnknownCondition
            ),
        };
    };

    const removeCancelledItems = (item: ProductItemSummary | DisputeItem) => {
        return item.state !== LineItemState.CANCELLED;
    };

    const filterSellItems = (item: ProductItemSummary) => {
        return !itemsToSellStatesToExclude.includes(item.state);
    };

    transactionSummary.itemsToBuy = transactionSummary.itemsToBuy
        .filter(removeCancelledItems)
        .map(updateItemConditionsToEnum);

    transactionSummary.itemsToSell = transactionSummary.itemsToSell
        .filter(filterSellItems)
        .map(updateItemConditionsToEnum);

    transactionSummary.itemsToReturn = transactionSummary.itemsToReturn.map(
        updateItemConditionsToEnum
    );

    transactionSummary.disputes =
        transactionSummary.disputes.filter(removeCancelledItems);

    if (transactionSummary.initialQuote) {
        transactionSummary.initialQuote.items =
            transactionSummary.initialQuote.items.map(
                updateItemConditionsToEnum
            );
    }

    if (transactionSummary.quote) {
        transactionSummary.quote.items = transactionSummary.quote.items.map(
            (item) => {
                const lineItems = [
                    ...transactionSummary.itemsToSell,
                    ...transactionSummary.itemsToReturn,
                ];
                const lineItem = lineItems.find(
                    (lineItem) => lineItem.id === item.lineItemId
                );

                return {
                    ...item,
                    lineItemState:
                        lineItem?.state ?? LineItemState.UNDEFINED_STATE,
                    condition: productConditionStringToEnum(
                        item.condition,
                        allowUnknownCondition
                    ),
                };
            }
        );
    }

    return transactionSummary;
}
